import { Unit, BorderStyle } from '../layout/constants'

import { styles as borderStyles, width as borderWidth } from './borders'
import { colorKeywords } from './colors'

const zeroNumeric = { unit: Unit.PX, value: 0 }
const DEFAULT_FONT_SIZE = 15
const DEFAULT_FONT_FAMILY = 'Times, Times New Roman, Georgia, serif'
const DEFAULT_LINE_HEIGHT = 1.2

const sizeProps = [
  'width',
  'height',
  'minWidth',
  'minHeight',
  'maxWidth',
  'maxHeight',
  'top',
  'right',
  'bottom',
  'left',

  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'borderTopLeftRadius',
  'borderTopRightRadius',
  'borderBottomRightRadius',
  'borderBottomLeftRadius',

  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',

  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',

  'fontSize',
]

const colorProps = [
  'backgroundColor',
  'borderTopColor',
  'borderRightColor',
  'borderBottomColor',
  'borderLeftColor',
]

const alternatives = {
  background: 'backgroundColor',
}

const shorthandParsers = {
  border(val, res) {
    const { color, style, width } = parseBorderShorthand(val)
    if (color != null) {
      Object.assign(res, applyBorderValueShortHand(color, 'Color'))
    }
    if (style != null) {
      Object.assign(res, applyBorderValueShortHand(style, 'Style'))
    }
    if (width != null) {
      Object.assign(res, applyBorderValueShortHand(width, 'Width'))
    }
  },
  borderTop(val, res) {
    Object.assign(res, applyBorderSideShortHand(val, 'Top'))
  },
  borderRight(val, res) {
    Object.assign(res, applyBorderSideShortHand(val, 'Right'))
  },
  borderBottom(val, res) {
    Object.assign(res, applyBorderSideShortHand(val, 'Bottom'))
  },
  borderLeft(val, res) {
    Object.assign(res, applyBorderSideShortHand(val, 'Left'))
  },
  borderWidth(val, res) {
    const splitted = splitAndFilter(val)
    const [borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth] = getSideValues(
      splitted,
      parseSizeValue,
    )
    Object.assign(res, { borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth })
  },
  borderStyle(val, res) {
    const splitted = splitAndFilter(val)
    const [borderTopStyle, borderRightStyle, borderBottomStyle, borderLeftStyle] =
      getSideValues(splitted)
    Object.assign(res, { borderTopStyle, borderRightStyle, borderBottomStyle, borderLeftStyle })
  },
  borderColor(val, res) {
    const splitted = splitAndFilter(val)
    const [borderTopColor, borderRightColor, borderBottomColor, borderLeftColor] = getSideValues(
      splitted,
      parseColor,
    )
    Object.assign(res, { borderTopColor, borderRightColor, borderBottomColor, borderLeftColor })
  },
  borderDashStyle(val, res) {
    const splitted = splitAndFilter(val)
    const borderDashStyleLength = parseSizeValue(splitted[0], true)
    const borderDashStyleSpace = parseSizeValue(splitted[1], true)
    if (borderDashStyleLength == null || borderDashStyleSpace == null) {
      return
    }
    Object.assign(res, { borderDashStyleLength, borderDashStyleSpace })
  },
  borderRadius(val, res) {
    const splitted = splitAndFilter(val)
    const [
      borderTopLeftRadius,
      borderTopRightRadius,
      borderBottomRightRadius,
      borderBottomLeftRadius,
    ] = getSideValues(splitted, parseSizeValue)
    Object.assign(res, {
      borderTopLeftRadius,
      borderTopRightRadius,
      borderBottomRightRadius,
      borderBottomLeftRadius,
    })
  },
  boxShadow(val, res) {
    const splitted = splitAndFilter(val)

    const color = parseColor(splitted[splitted.length - 1])
    if (color != null) {
      res.boxShadowColor = color
      splitted.pop()
    }

    let boxShadowOffsetX = parseSizeValue(splitted[0], true)
    let boxShadowOffsetY = parseSizeValue(splitted[1], true)
    if (boxShadowOffsetX == null || boxShadowOffsetY == null) {
      boxShadowOffsetX = null
      boxShadowOffsetY = null
    }
    const boxShadowBlurRadius = parseSizeValue(splitted[2])
    Object.assign(res, { boxShadowOffsetX, boxShadowOffsetY, boxShadowBlurRadius })
  },
  font(val, res) {
    const [size, ...fontFamily] = splitAndFilter(val)
    if (fontFamily.length === 0) {
      return
    }

    const [fontSizeVal, lineHeightVal] = size.split('/')
    const fontSize = parseSizeValue(fontSizeVal)
    const fontObject = { fontSize, fontFamily: fontFamily.join(' ') }

    if (lineHeightVal != null) {
      this.lineHeight(lineHeightVal, fontObject)
    }

    Object.assign(res, fontObject)
  },
  lineHeight(val, res) {
    const value = val * 100
    if (Number.isFinite(value)) {
      res.lineHeight = { unit: Unit.PERCENTAGE, value }
      return
    }

    val = parseSizeValue(val, true)
    if (val != null) {
      res.lineHeight = val
    }
  },
  margin(val, res) {
    const splitted = splitAndFilter(val)
    const [marginTop, marginRight, marginBottom, marginLeft] = getSideValues(
      splitted,
      parseSizeValue,
    )
    Object.assign(res, { marginTop, marginRight, marginBottom, marginLeft })
  },
  padding(val, res) {
    const splitted = splitAndFilter(val)
    const [paddingTop, paddingRight, paddingBottom, paddingLeft] = getSideValues(
      splitted,
      parseSizeValue,
    )
    Object.assign(res, { paddingTop, paddingRight, paddingBottom, paddingLeft })
  },
}

const applyBorderPropShortHand = (val, prop, side) => ({
  [`border${side}${prop}`]: val,
})

const applyBorderSideShortHand = (val, side) => {
  const res = {}
  const { color, style, width } = parseBorderShorthand(val)
  if (color != null) {
    Object.assign(res, applyBorderPropShortHand(color, 'Color', side))
  }
  if (style != null) {
    Object.assign(res, applyBorderPropShortHand(style, 'Style', side))
  }
  if (width != null) {
    Object.assign(res, applyBorderPropShortHand(width, 'Width', side))
  }

  return res
}

const applyBorderValueShortHand = (val, prop) => ({
  [`borderTop${prop}`]: val,
  [`borderRight${prop}`]: val,
  [`borderBottom${prop}`]: val,
  [`borderLeft${prop}`]: val,
})

const getSideValues = (val, processor = (val) => val) => {
  if (!val) {
    const value = processor(0)
    return [value, value, value, value, value, value]
  }

  switch (val.length) {
    case 1: {
      const value = processor(val[0])
      return [value, value, value, value]
    }
    case 2: {
      const verticalValue = processor(val[0])
      const horizontalValue = processor(val[1])
      return [verticalValue, horizontalValue, verticalValue, horizontalValue]
    }
    case 3: {
      const topValue = processor(val[0])
      const horizontalValue = processor(val[1])
      const bottomValue = processor(val[2])
      return [topValue, horizontalValue, bottomValue, horizontalValue]
    }
    default:
      return [processor(val[0]), processor(val[1]), processor(val[2]), processor(val[3])]
  }
}

const parseBorderShorthand = (val) => {
  const res = {}
  const splitted = splitAndFilter(val)
  for (const prop of splitted) {
    if (borderStyles[prop] != null) {
      res.style = borderStyles[prop]
      continue
    }

    const width = borderWidth[prop] || parseSizeValue(prop, true)
    if (width != null) {
      res.width = width
      continue
    }

    const color = parseColor(prop)
    if (color != null) {
      res.color = color
    }
  }

  return res
}

const parseColor = (val = '') => {
  if (val.indexOf('#') !== -1) {
    return val
  }

  if (val.indexOf('rgb') !== -1) {
    return val
  }

  return colorKeywords[val] ? colorKeywords[val] : null
}

const numUnitRegexp = /^(-?\d+(?:\.?\d+|\d*))(px|%)?$/

const parseSizeValue = (val, returnNull = false) => {
  const numVal = val * 1
  if (Number.isFinite(numVal)) {
    return { unit: Unit.PX, value: numVal }
  }

  const matched = `${val}`.match(numUnitRegexp)
  if (!matched) {
    return returnNull ? null : zeroNumeric
  }

  return { unit: matched[2], value: matched[1] * 1 }
}

const splitAndFilter = (val) => {
  let stringVal = `${val}`
  // Removing spaces from rgba values
  stringVal = stringVal.replace(/rgba?\(.*\)/g, (subStr) => subStr.replace(/\s/g, ''))

  return stringVal.split(/\s/).filter((e) => e)
}

const computeShadowSize = (offset, blur) => {
  if (offset == null) {
    return [0, 0]
  }

  let startSize = blur
  let endSize = blur
  const offsetValue = offset.value
  const absOffset = Math.abs(offsetValue)
  if (offsetValue > 0) {
    startSize = Math.max(startSize - absOffset, 0)
    endSize += absOffset
  } else {
    startSize += absOffset
    endSize = Math.max(endSize - absOffset, 0)
  }

  return [startSize, endSize]
}

const computeValues = (style) => {
  style.font = `${style.fontSize.value}px/${
    style.lineHeight.unit === Unit.PERCENTAGE
      ? style.lineHeight.value / 100
      : style.lineHeight.value + 'px'
  } ${style.fontFamily}`

  style.borderTopWidth.computed =
    style.borderTopStyle !== BorderStyle.NONE ? style.borderTopWidth.value : 0
  style.borderRightWidth.computed =
    style.borderRightStyle !== BorderStyle.NONE ? style.borderRightWidth.value : 0
  style.borderBottomWidth.computed =
    style.borderBottomStyle !== BorderStyle.NONE ? style.borderBottomWidth.value : 0
  style.borderLeftWidth.computed =
    style.borderLeftStyle !== BorderStyle.NONE ? style.borderLeftWidth.value : 0

  style.withBoxShadow = style.boxShadowOffsetX != null
  if (style.withBoxShadow) {
    const yShadowSize = computeShadowSize(style.boxShadowOffsetY, style.boxShadowBlurRadius.value)
    const xShadowSize = computeShadowSize(style.boxShadowOffsetX, style.boxShadowBlurRadius.value)
    style.boxShadowSizeTop = yShadowSize[0]
    style.boxShadowSizeBottom = yShadowSize[1]
    style.boxShadowSizeLeft = xShadowSize[0]
    style.boxShadowSizeRight = xShadowSize[1]
  }

  style.borderTopSize = Math.max(
    style.borderTopWidth.computed,
    style.borderTopLeftRadius.value,
    style.borderTopRightRadius.value,
  )
  style.borderBottomSize = Math.max(
    style.borderBottomWidth.computed,
    style.borderBottomLeftRadius.value,
    style.borderBottomRightRadius.value,
  )
  style.borderLeftSize = Math.max(
    style.borderLeftWidth.computed,
    style.borderTopLeftRadius.value,
    style.borderBottomLeftRadius.value,
  )
  style.borderRightSize = Math.max(
    style.borderRightWidth.computed,
    style.borderTopRightRadius.value,
    style.borderBottomRightRadius.value,
  )
}

export const createDefaultStyle = () => ({
  position: 'static',
  width: null,
  height: null,

  borderTopWidth: borderWidth.medium,
  borderRightWidth: borderWidth.medium,
  borderBottomWidth: borderWidth.medium,
  borderLeftWidth: borderWidth.medium,

  borderTopStyle: 'none',
  borderRightStyle: 'none',
  borderBottomStyle: 'none',
  borderLeftStyle: 'none',
  borderDashStyleLength: null,
  borderDashStyleSpace: null,

  borderTopColor: colorKeywords.black,
  borderRightColor: colorKeywords.black,
  borderBottomColor: colorKeywords.black,
  borderLeftColor: colorKeywords.black,

  borderTopLeftRadius: zeroNumeric,
  borderTopRightRadius: zeroNumeric,
  borderBottomRightRadius: zeroNumeric,
  borderBottomLeftRadius: zeroNumeric,

  boxShadowOffsetX: null,
  boxShadowOffsetY: null,
  boxShadowBlurRadius: zeroNumeric,
  boxShadowSizeTop: 0,
  boxShadowSizeBottom: 0,
  boxShadowSizeLeft: 0,
  boxShadowSizeRight: 0,

  marginTop: zeroNumeric,
  marginRight: zeroNumeric,
  marginBottom: zeroNumeric,
  marginLeft: zeroNumeric,

  paddingTop: zeroNumeric,
  paddingRight: zeroNumeric,
  paddingBottom: zeroNumeric,
  paddingLeft: zeroNumeric,

  font: `${DEFAULT_FONT_SIZE}px/${DEFAULT_LINE_HEIGHT} ${DEFAULT_FONT_FAMILY}`,
  fontSize: { unit: Unit.PX, value: DEFAULT_FONT_SIZE },
  fontFamily: DEFAULT_FONT_FAMILY,
  // fontStyle: null,
  // fontVariant: null,
  // fontWeight: null,
  // fontStretch: null,
  lineHeight: { unit: Unit.PERCENTAGE, value: DEFAULT_LINE_HEIGHT * 100 },
})

export default function parse(style = {}) {
  const res = createDefaultStyle()
  res.percentageValuesExists = false
  for (const prop in style) {
    const altProp = alternatives[prop] ? alternatives[prop] : prop
    const val = style[prop]
    let parsed
    if (sizeProps.includes(altProp)) {
      parsed = parseSizeValue(val)
      if (parsed.unit === Unit.PERCENTAGE) {
        res.percentageValuesExists = true
      }
    }
    if (colorProps.includes(altProp)) {
      parsed = parseColor(val)
    }

    if (shorthandParsers[prop] != null) {
      shorthandParsers[prop](val, res)
      continue
    }

    if (parsed) {
      res[altProp] = parsed
    } else {
      res[prop] = val
    }
  }

  computeValues(res)

  return res
}
